import React, { Fragment, useEffect, useState } from 'react'
import { PendingProcessHearingType } from 'types/pending-process-hearing.type'
import { Box, Button, DialogProps, IconButton, Tooltip, Typography } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import {
  AdolescenteIntegraJudicialType,
  AdolescentesIntegraJudicialModal,
} from 'pages/ViewPendingProcessHearing/modal/AdolescentesIntegraJudicialModal'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import { STATUS_OPTIONS } from 'pages/ViewPendingProcessHearing'
import dayjs from 'dayjs'
import { useAuthContext } from 'contexts/auth.context'
import { FieldArray, UseFieldArrayAppend } from 'react-hook-form/dist/types/fieldArray'
import { FieldArrayPath } from 'react-hook-form/dist/types/path'
import { FieldValues, useFormContext } from 'react-hook-form'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import DialogContent from '@mui/material/DialogContent'
import Dialog from '@mui/material/Dialog'
import BalanceIcon from '@mui/icons-material/Balance'

const formatarData = (data?: string | null): string => {
  return data && dayjs(data).isValid() ? dayjs(data).format('DD/MM/YYYY HH:mm') : 'N/A'
}

interface ViewPendingProcessHearingHeaderProps<TFieldValues extends FieldValues> {
  processo: PendingProcessHearingType
  onAdd: (data: AdolescenteIntegraJudicialType[]) => Promise<void>
  append: UseFieldArrayAppend<TFieldValues, FieldArrayPath<TFieldValues>>
}

export function ViewPendingProcessHearingHeader<TFieldValues extends FieldValues>({
  processo,
  onAdd,
  append,
}: ViewPendingProcessHearingHeaderProps<TFieldValues>) {
  const { setValue } = useFormContext<PendingProcessHearingType>()

  const [dataUltimaAtualizacaoIntegraJudicial, setDataUltimaAtualizacaoIntegraJudicial] = useState<
    string | null | undefined
  >('N/A')

  const { handleHasPermission } = useAuthContext()

  useEffect(() => {
    setDataUltimaAtualizacaoIntegraJudicial(processo.dataUltimaAtualizacaoIntegraJudicial)
  }, [processo])

  const handleChangeDataUltimaAtualizacaoIntegraJudicial = async () => {
    setDataUltimaAtualizacaoIntegraJudicial(dayjs().format('YYYY-MM-DD HH:mm:ss').toString())
  }

  useEffect(() => {
    if (dataUltimaAtualizacaoIntegraJudicial && dataUltimaAtualizacaoIntegraJudicial !== 'N/A')
      setValue('dataUltimaAtualizacaoIntegraJudicial', dataUltimaAtualizacaoIntegraJudicial)
  }, [dataUltimaAtualizacaoIntegraJudicial])

  return (
    <Fragment>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        flexWrap={'wrap'}
      >
        <Box display={'flex'} alignItems={'center'}>
          <Typography variant={'body1'} color={'secondary'}>
            {`${processo.categoria} ${processo.numeroProcesso}`}
          </Typography>
          {processo.notificacoes && processo.notificacoes.length > 0 && (
            <Box sx={{ marginLeft: '1rem' }}>
              <Tooltip
                title={
                  <div>
                    {processo.notificacoes.map((notificaco, i) => (
                      <div key={i}>{notificaco}</div>
                    ))}
                  </div>
                }
              >
                <WarningIcon sx={{ color: '#fbc02d' }} />
              </Tooltip>
            </Box>
          )}
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Typography variant={'body1'} color={'secondary'}>
            {`Status Geral: ${STATUS_OPTIONS[processo.status]}`}
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          <Typography variant='body1' color='secondary'>
            {`Data da última atualização do Integra Judicial: ${formatarData(
              dataUltimaAtualizacaoIntegraJudicial,
            )}`}
          </Typography>
        </Box>
        <Box display={'flex'} alignItems={'center'} gap={2}>
          <AdolescentesIntegraJudicialModal
            onAdd={onAdd}
            onChangeDate={handleChangeDataUltimaAtualizacaoIntegraJudicial}
          />
          {handleHasPermission(Funcionalidades.integraPolicial.adolescente.adicionar) && (
            <ModalAdicionarAdolescente append={append} />
          )}
        </Box>
      </Box>
    </Fragment>
  )
}

interface ModalAdicionarAdolescenteProps<TFieldValues extends FieldValues> {
  append: UseFieldArrayAppend<TFieldValues, FieldArrayPath<TFieldValues>>
}

function ModalAdicionarAdolescente<TFieldValues extends FieldValues>({
  append,
}: ModalAdicionarAdolescenteProps<TFieldValues>) {
  const [fullWidth] = useState(true)
  const [maxWidth] = useState<DialogProps['maxWidth']>('md')

  const { watch } = useFormContext<PendingProcessHearingType>()
  const dataUltimaAtualizacaoIntegraJudicialWatch = watch('dataUltimaAtualizacaoIntegraJudicial')

  const [open, setOpen] = useState<boolean>(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Fragment>
      <Tooltip title='Adicionar adolescente.' placement={'top'}>
        <IconButton
          size='medium'
          onClick={() => {
            if (dataUltimaAtualizacaoIntegraJudicialWatch) {
              append({
                status: 'PENDENTE',
                nomeAdolescente: '',
              } as FieldArray<TFieldValues, FieldArrayPath<TFieldValues>>)
            } else {
              handleOpen()
            }
          }}
        >
          <PersonAddAltIcon fontSize='inherit' />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} fullWidth={fullWidth} maxWidth={maxWidth}>
        <DialogTitle>
          <Typography variant={'h6'} color={'primary.main'}>
            Selecione os adolescentes disponíveis para Oitiva Informal
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <p>
            Antes de adicionar um adolescente manualmente, verifique se os dados dos adolescentes
            constam no Integra Judicial. Consulte clicando no botão{'  '}
            <Tooltip title='Integra Judicial.' placement={'top'}>
              <BalanceIcon fontSize='inherit' sx={{ height: '1.25rem', width: '1.25rem' }} />
            </Tooltip>
            .
          </p>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'end'}
            marginTop={'1rem'}
            gap={2}
          >
            <Button variant={'text'} color={'secondary'} size={'large'} onClick={handleClose}>
              Fechar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Fragment>
  )
}
